import { useEffect, useRef, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { trpc, createTrpcClient } from '@/shared/lib/trpc'
import { queryClient } from '@/shared/lib/query-client'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useMessagesStore } from '@/shared/stores/messages.store'
import { useTabsStore } from '@/shared/stores/tabs.store'
import { wsClient } from '@/shared/lib/websocket'
import { subscriptionManager } from '@/shared/lib/subscriptions'
import { useConnectionStore } from '@/shared/stores/connection.store'

const trpcClient = createTrpcClient()

function OAuthDetector() {
  const setAuth = useAuthStore(s => s.setAuth)

  useEffect(() => {
    // Le token OAuth arrive dans le FRAGMENT (#token=...), jamais envoyé au serveur
    // donc absent des logs. L'erreur reste dans la query (?error=...) côté serveur.
    const searchParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.slice(1))
    const token = hashParams.get('token')
    const oauthError = searchParams.get('error')

    if (oauthError) {
      window.history.replaceState({}, '', '/app/login?auth_error=' + encodeURIComponent(oauthError))
      return
    }

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1])) as {
          email: string
          sub: string
          name?: string
          role?: string
        }
        setAuth(
          { id: payload.sub, email: payload.email, name: payload.name ?? '', role: (payload.role ?? 'user') as 'admin' | 'user' },
          token,
        )
        window.history.replaceState({}, '', '/app/chat')
      } catch {
        /* token invalide — rester sur login */
      }
    }
  }, [setAuth])

  return null
}

function CentrifugeConnector() {
  // Dépendance volontairement sur isAuthenticated (booléen stable, ne bascule qu'au
  // login/logout) et non sur accessToken (chaîne JWT qui change d'identité à chaque
  // refresh de token, ~toutes les 15 min). Le token de connexion Centrifugo est de
  // toute façon minté à la demande via getToken (websocket.ts), qui relit l'access
  // token FRAIS dans le store à CHAQUE appel — aucun reconnect n'est nécessaire quand
  // seule la valeur du token change.
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)

  useEffect(() => {
    if (isAuthenticated) {
      // connect() et start() sont idempotents (ensureClient réutilise l'instance
      // existante, subscriptionManager.start garde `started`) : aucun effet si déjà actifs.
      wsClient.connect()
      subscriptionManager.start()
    } else {
      // Vrai logout uniquement. stop() et disconnect() sont idempotents (gardes
      // internes) : aucun effet si déjà arrêtés — safe même au tout premier rendu
      // avant login.
      subscriptionManager.stop()
      wsClient.disconnect()
    }
    // Pas de fonction de cleanup ici : la déconnexion ne doit se produire QUE sur la
    // transition isAuthenticated→false ci-dessus, jamais sur un simple re-render ou
    // remount du composant (qui ne re-déclenche pas cet effet tant que la dépendance
    // ne change pas).
  }, [isAuthenticated])

  return null
}

/**
 * Boot-time session opener. Called once at startup.
 * - If no tabs and sessions exist: opens the most recent non-closed session.
 * - If no tabs and no sessions at all: nothing to do (user will create via sidebar).
 * - If tabs already exist: TabSessionValidator handles reconciliation — nothing to do here.
 *
 * Deps: [sessions] only — tabs checked via getState() to avoid re-subscription (E5 memory leak fix).
 */
function SessionBootstrap() {
  const accessToken = useAuthStore(s => s.accessToken)

  const { data: sessions } = trpc.sessions.list.useQuery(
    {},
    { enabled: !!accessToken },
  )

  useEffect(() => {
    if (!sessions) return
    // Read tabs directly — no selector subscription, avoids cascade re-renders on tab changes
    if (useTabsStore.getState().tabs.length > 0) return

    if (sessions.length === 0) {
      // No sessions in DB — nothing to do, user creates via sidebar "+" button
      return
    }

    // Open the most recent non-closed session (list is already sorted by last_activity desc)
    const lastActive = sessions.find(s => s.status !== 'closed')
    if (lastActive) {
      useTabsStore.getState().openExistingSession(lastActive.id, lastActive.title ?? 'Sans titre')
    }
  }, [sessions]) // eslint-disable-line react-hooks/exhaustive-deps

  return null
}

/**
 * Reconcile tabs from localStorage against server sessions on startup.
 * Same useQuery key + enabled as SessionBootstrap — React Query deduplicates (single network fetch).
 * Tabs pointing to non-existent or closed sessions are removed.
 * Tab titles that differ from the BDD are updated.
 */
function TabSessionValidator() {
  const accessToken = useAuthStore(s => s.accessToken)

  const { data: activeSessions } = trpc.sessions.list.useQuery(
    {},
    { enabled: !!accessToken },
  )

  useEffect(() => {
    if (!activeSessions) return

    const sessionMap = new Map(activeSessions.map(s => [s.id, s]))

    const currentTabs = useTabsStore.getState().tabs
    for (const tab of currentTabs) {
      if (!tab.sessionId) continue

      const session = sessionMap.get(tab.sessionId)
      if (!session || session.status === 'closed') {
        useTabsStore.getState().removeTab(tab.id)
      } else if (tab.title !== session.title) {
        useTabsStore.getState().updateTabTitle(tab.id, session.title ?? 'Sans titre')
      }
    }
  }, [activeSessions])

  return null
}

/**
 * Invalidates React Query caches after WS reconnection or page visibility change.
 * Two roles:
 * 1. WS reconnect: listens connection:status — invalidates sessions.list + active session messages.list on transition to 'online'
 * 2. iOS background: listens visibilitychange — invalidates sessions.list on foreground return
 */
function ReconnectSyncer() {
  const prevStatus = useRef<string>('online')
  const utils = trpc.useUtils()
  const status = useConnectionStore(s => s.status)

  // Role 1: WS reconnect → invalidate sessions + active session messages
  useEffect(() => {
    if (status === 'online' && prevStatus.current !== 'online') {
      void utils.sessions.list.invalidate()

      // Invalidate messages for the active session — sauf si streaming en cours
      const { tabs, activeTabId } = useTabsStore.getState()
      const activeTab = tabs.find(t => t.id === activeTabId)
      if (activeTab?.sessionId) {
        const sessionState = useMessagesStore.getState().sessions[activeTab.sessionId]
        const isStreamingNow = sessionState?.isStreaming ?? false
        if (!isStreamingNow) {
          void utils.messages.list.invalidate({ sessionId: activeTab.sessionId })
        }
      }
    }
    prevStatus.current = status
  }, [status, utils])

  // Role 2: visibilitychange (iOS background) → invalidate sessions as safety net
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void utils.sessions.list.invalidate()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [utils])

  return null
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <OAuthDetector />
        <CentrifugeConnector />
        <TabSessionValidator />
        <SessionBootstrap />
        <ReconnectSyncer />
        {children}
      </QueryClientProvider>
    </trpc.Provider>
  )
}
